import { useState, useEffect } from 'react';
import { useSearchParams, useNavigate, Link } from 'react-router-dom';
import { useLang } from '../lib/lang';
import { resetPassword, verifyResetToken } from '../lib/api';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Lock, Loader2, CheckCircle2, XCircle, Eye, EyeOff, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';
import Header from '../components/Header';
import Footer from '../components/Footer';

export default function ResetPasswordPage() {
  const { t } = useLang();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('token') || '';

  const [verifying, setVerifying] = useState(true);
  const [valid, setValid] = useState(false);
  const [userEmail, setUserEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [show, setShow] = useState({ pwd: false, confirm: false });
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    if (!token) {
      setVerifying(false);
      setValid(false);
      return;
    }
    verifyResetToken(token)
      .then((res) => {
        if (res.data?.valid) {
          setValid(true);
          setUserEmail(res.data?.user_email || '');
        } else {
          setValid(false);
        }
      })
      .catch(() => setValid(false))
      .finally(() => setVerifying(false));
  }, [token]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (password.length < 8) {
      toast.error(t('passwordTooShort') || 'Le mot de passe doit contenir au moins 8 caractères');
      return;
    }
    if (password !== confirm) {
      toast.error(t('passwordMismatch') || 'Les mots de passe ne correspondent pas');
      return;
    }
    setSubmitting(true);
    try {
      await resetPassword({ token, new_password: password });
      setSuccess(true);
      setTimeout(() => navigate('/login'), 2500);
    } catch (e) {
      toast.error(e?.response?.data?.detail || (t('errorOccurred') || 'Une erreur est survenue'));
    } finally {
      setSubmitting(false);
    }
  };

  // Password strength feedback
  const checks = {
    length: password.length >= 8,
    upper: /[A-Z]/.test(password),
    lower: /[a-z]/.test(password),
    digit: /\d/.test(password),
    special: /[^A-Za-z0-9]/.test(password),
  };
  const strength = Object.values(checks).filter(Boolean).length;
  const strengthLabel = ['Très faible', 'Faible', 'Moyen', 'Bon', 'Très fort'][Math.max(0, strength - 1)] || 'Très faible';
  const strengthColors = ['bg-red-500', 'bg-orange-500', 'bg-amber-500', 'bg-emerald-500', 'bg-emerald-600'];

  return (
    <div className="min-h-screen flex flex-col bg-rolex-green">
      <Header />
      <main className="flex-1 flex items-center justify-center p-4 py-12">
        <div className="w-full max-w-md">
          <Card className="bg-white border-2 border-[hsl(var(--gold)/0.30)] shadow-2xl">
            <CardContent className="p-7 sm:p-9">
              {verifying ? (
                <div className="text-center py-10">
                  <Loader2 className="w-10 h-10 animate-spin text-gold mx-auto mb-3" />
                  <p className="text-rolex-green/70 text-sm">Vérification du lien…</p>
                </div>
              ) : !valid ? (
                <div className="text-center">
                  <div className="w-20 h-20 rounded-full bg-red-500 mx-auto mb-5 flex items-center justify-center shadow-lg shadow-red-500/30">
                    <XCircle className="w-11 h-11 text-white" strokeWidth={2.5} />
                  </div>
                  <h1 className="font-display text-2xl font-extrabold text-red-700 mb-2">
                    Lien invalide ou expiré
                  </h1>
                  <p className="text-rolex-green/70 text-sm mb-5 leading-relaxed">
                    Ce lien de réinitialisation n'est plus valide. Cela peut arriver si :
                  </p>
                  <ul className="text-left text-xs text-rolex-green/70 space-y-1 mb-6 max-w-xs mx-auto">
                    <li>• Le lien est expiré (validité 1h)</li>
                    <li>• Vous l'avez déjà utilisé</li>
                    <li>• Une nouvelle demande a été faite (qui invalide la précédente)</li>
                  </ul>
                  <Link to="/forgot-password">
                    <Button className="btn-gold w-full h-12 font-bold">
                      Demander un nouveau lien
                    </Button>
                  </Link>
                  <Link to="/login" className="block mt-3 text-xs text-rolex-green/55 hover:text-gold">
                    Retour à la connexion
                  </Link>
                </div>
              ) : success ? (
                <div className="text-center">
                  <div className="w-20 h-20 rounded-full bg-emerald-500 mx-auto mb-5 flex items-center justify-center shadow-lg shadow-emerald-500/30">
                    <CheckCircle2 className="w-11 h-11 text-white" strokeWidth={2.5} />
                  </div>
                  <h1 className="font-display text-2xl font-extrabold text-emerald-700 mb-2">
                    Mot de passe modifié !
                  </h1>
                  <p className="text-rolex-green/70 text-sm mb-5">
                    Vous allez être redirigé vers la page de connexion…
                  </p>
                  <Loader2 className="w-6 h-6 animate-spin text-gold mx-auto" />
                </div>
              ) : (
                <>
                  <div className="text-center mb-6">
                    <div className="w-16 h-16 rounded-full bg-gold-gradient mx-auto mb-4 flex items-center justify-center shadow-lg shadow-gold/40">
                      <Lock className="w-8 h-8 text-rolex-green" strokeWidth={2.2} />
                    </div>
                    <h1 className="font-display text-2xl sm:text-3xl font-extrabold text-rolex-green mb-2">
                      Nouveau mot de passe
                    </h1>
                    {userEmail && (
                      <p className="text-rolex-green/65 text-xs mb-1">Pour <strong className="text-rolex-green">{userEmail}</strong></p>
                    )}
                    <p className="text-rolex-green/70 text-sm">
                      Choisissez un mot de passe sécurisé pour votre compte.
                    </p>
                  </div>

                  <form onSubmit={handleSubmit} className="space-y-4">
                    <div className="space-y-2">
                      <Label htmlFor="password" className="text-rolex-green font-semibold">
                        Nouveau mot de passe
                      </Label>
                      <div className="relative">
                        <Lock className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-rolex-green/45" />
                        <Input
                          id="password"
                          type={show.pwd ? 'text' : 'password'}
                          autoComplete="new-password"
                          value={password}
                          onChange={(e) => setPassword(e.target.value)}
                          placeholder="Au moins 8 caractères"
                          className="pl-9 pr-10 h-12 border-gold/30 focus:border-gold"
                          required
                          data-testid="reset-password-input"
                        />
                        <button type="button" onClick={() => setShow({ ...show, pwd: !show.pwd })} className="absolute right-3 top-1/2 -translate-y-1/2 text-rolex-green/45 hover:text-rolex-green">
                          {show.pwd ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                        </button>
                      </div>
                      {password && (
                        <div className="space-y-1.5">
                          <div className="flex gap-1">
                            {[1, 2, 3, 4, 5].map((i) => (
                              <div key={i} className={`flex-1 h-1.5 rounded-full ${i <= strength ? strengthColors[strength - 1] : 'bg-stone-200'}`} />
                            ))}
                          </div>
                          <p className="text-[10px] text-rolex-green/65">Force : <strong>{strengthLabel}</strong></p>
                          {strength < 3 && (
                            <div className="text-[10px] text-rolex-green/55 space-y-0.5 mt-1">
                              {!checks.length && <p>• Au moins 8 caractères</p>}
                              {!checks.upper && <p>• Une majuscule</p>}
                              {!checks.lower && <p>• Une minuscule</p>}
                              {!checks.digit && <p>• Un chiffre</p>}
                              {!checks.special && <p>• Un caractère spécial recommandé</p>}
                            </div>
                          )}
                        </div>
                      )}
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="confirm" className="text-rolex-green font-semibold">
                        Confirmer le mot de passe
                      </Label>
                      <div className="relative">
                        <Lock className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-rolex-green/45" />
                        <Input
                          id="confirm"
                          type={show.confirm ? 'text' : 'password'}
                          autoComplete="new-password"
                          value={confirm}
                          onChange={(e) => setConfirm(e.target.value)}
                          placeholder="Retapez votre mot de passe"
                          className={`pl-9 pr-10 h-12 border-gold/30 focus:border-gold ${confirm && password !== confirm ? 'border-red-300 focus:border-red-500' : ''}`}
                          required
                          data-testid="reset-password-confirm"
                        />
                        <button type="button" onClick={() => setShow({ ...show, confirm: !show.confirm })} className="absolute right-3 top-1/2 -translate-y-1/2 text-rolex-green/45 hover:text-rolex-green">
                          {show.confirm ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                        </button>
                      </div>
                      {confirm && password !== confirm && (
                        <p className="text-[10px] text-red-600 flex items-center gap-1"><AlertTriangle className="w-3 h-3" /> Les mots de passe ne correspondent pas</p>
                      )}
                    </div>

                    <Button
                      type="submit"
                      disabled={submitting || password.length < 8 || password !== confirm}
                      className="btn-gold w-full h-12 font-bold gap-2"
                      data-testid="reset-password-submit"
                    >
                      {submitting ? <Loader2 className="w-5 h-5 animate-spin" /> : <CheckCircle2 className="w-5 h-5" />}
                      {submitting ? 'Modification…' : 'Modifier mon mot de passe'}
                    </Button>
                  </form>
                </>
              )}
            </CardContent>
          </Card>
        </div>
      </main>
      <Footer />
    </div>
  );
}
